/**
 * T3 화재잔해 클래스 모드 훅 — EIP · 클래스 스크리닝 · 클래스 판정.
 *
 * 실데이터 원칙: API 가 없다고 하면 없는 대로 보여준다 — 어떤 훅도
 * 가짜 데이터로 메우지 않는다.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { analysesApi, spectrumApi } from "@/services/api";
import type {
  ClassAssessment,
  ClassAssessmentList,
  EIPResponse,
  ILClassScreening,
} from "@/types";

/** 시료의 추출이온 프로파일 (기본: 판별표 스크리닝 이온 전부).
 *
 * ions 를 쓰면 queryKey 에도 차원이 들어간다 — 같은 시료의 다른 이온
 * 조합이 캐시를 덮어쓰지 않는다 (교차 리뷰).
 */
export function useSampleEIP(sampleId?: number, ions?: string) {
  return useQuery<EIPResponse>({
    queryKey: ["spectrum", "eip", sampleId, ions ?? "default"],
    queryFn: async () => {
      const response = await spectrumApi.getEIP(sampleId as number, ions);
      return response.data;
    },
    enabled: !!sampleId,
  });
}

/** 완료된 분석의 IL 클래스 스크리닝 (조건 충족 계산 — 판정 아님). */
export function useILClassScreening(analysisId?: number) {
  return useQuery<ILClassScreening>({
    queryKey: ["analysis", "il-class-screening", analysisId],
    queryFn: async () => {
      const response = await analysesApi.getILClassScreening(
        analysisId as number,
      );
      return response.data;
    },
    enabled: !!analysisId,
  });
}

/** 분석 1건의 클래스 판정 목록 (현재 + 이력). */
export function useClassAssessments(analysisId?: number) {
  return useQuery<ClassAssessmentList>({
    queryKey: ["analysis", "class-assessments", analysisId],
    queryFn: async () => {
      const response = await analysesApi.getClassAssessments(
        analysisId as number,
      );
      return response.data;
    },
    enabled: !!analysisId,
  });
}

/** 클래스 판정 기록 — append-only. 성공 시 판정 목록만 무효화한다. */
export function useCreateClassAssessment(analysisId?: number) {
  const queryClient = useQueryClient();
  return useMutation<
    ClassAssessment,
    Error,
    { scheme_id: string; class_key: string; status: string; rationale: string }
  >({
    mutationFn: async (payload) => {
      const response = await analysesApi.createClassAssessment(
        analysisId as number,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["analysis", "class-assessments", analysisId],
      });
    },
  });
}
